import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CreditCard, Check, AlertTriangle, Loader2, Download, 
  Calendar, TrendingUp, Zap, Building2, Users, Receipt,
  RefreshCw, ArrowRight, Clock, Construction
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const Billing = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billingCycle, setBillingCycle] = useState('MONTHLY');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes, usageRes, invoicesRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/current'),
        api.get('/subscriptions/usage'),
        api.get('/subscriptions/invoices')
      ]);
      
      setPlans(plansRes.data);
      setCurrentSubscription(subRes.data.subscription);
      setCurrentPlan(subRes.data.plan);
      setUsage(usageRes.data);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (paise, includeGST = false) => {
    const amount = paise / 100;
    const finalAmount = includeGST ? amount * 1.18 : amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(finalAmount);
  };

  const handleSubscribe = async (plan) => {
    setSelectedPlan(plan);
    setShowUpgradeDialog(true);
  };

  const confirmSubscription = async () => {
    if (!selectedPlan) return;
    
    setSubscribing(true);
    try {
      const response = await api.post(`/subscriptions/subscribe?plan_id=${selectedPlan.plan_id}&billing_cycle=${billingCycle}`);
      
      // Open Razorpay checkout
      const options = {
        key: response.data.razorpay_key,
        subscription_id: response.data.razorpay_subscription_id,
        name: 'FinOrchest',
        description: `${selectedPlan.name} - ${billingCycle} Subscription`,
        handler: function(response) {
          toast.success('Subscription activated successfully!');
          setShowUpgradeDialog(false);
          fetchData();
        },
        prefill: {
          email: user?.email,
          name: user?.name
        },
        theme: {
          color: '#1e3a8a'
        }
      };
      
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // Fallback - redirect to payment link
        if (response.data.short_url) {
          window.open(response.data.short_url, '_blank');
        }
        toast.info('Complete payment in the opened window');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      DEMO: 'bg-purple-100 text-purple-700',
      ACTIVE: 'bg-green-100 text-green-700',
      PAST_DUE: 'bg-red-100 text-red-700',
      CANCELLED: 'bg-gray-100 text-gray-700',
      EXPIRED: 'bg-orange-100 text-orange-700'
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const isDemo = currentSubscription?.status === 'DEMO';
  const demoExpired = isDemo && currentSubscription?.demo_expires_at && 
    new Date(currentSubscription.demo_expires_at) < new Date();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
          <p className="text-gray-500 mt-1">Manage your subscription plan and billing</p>
        </div>

        {/* Demo Alert */}
        {isDemo && (
          <Alert className={demoExpired ? "bg-red-50 border-red-200" : "bg-purple-50 border-purple-200"}>
            <AlertTriangle className={`h-4 w-4 ${demoExpired ? 'text-red-600' : 'text-purple-600'}`} />
            <AlertDescription className={demoExpired ? 'text-red-700' : 'text-purple-700'}>
              {demoExpired ? (
                <>Your 2-day demo has expired. Subscribe now to continue using FinOrchest.</>
              ) : (
                <>You're on a 2-day demo account. This is a dummy account to explore features - you cannot link a real bank account. Subscribe to unlock all features.</>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Current Plan & Usage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Plan */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Current Plan</CardTitle>
                <Badge className={getStatusBadge(currentSubscription?.status)}>
                  {currentSubscription?.status || 'No Plan'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{currentPlan?.name || 'No Plan'}</h3>
                  <p className="text-gray-500 text-sm">{currentPlan?.description}</p>
                </div>
              </div>
              
              {currentPlan && currentPlan.tier !== 'DEMO' && (
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{currentPlan.transactions_per_month}</p>
                    <p className="text-xs text-gray-500">Transactions/mo</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{currentPlan.departments_limit}</p>
                    <p className="text-xs text-gray-500">Departments</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{currentPlan.users_limit}</p>
                    <p className="text-xs text-gray-500">Users</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage with Rollover */}
          <Card>
            <CardHeader>
              <CardTitle>This Period's Usage</CardTitle>
              <CardDescription>
                {usage?.period_start && (
                  <>Period: {new Date(usage.period_start).toLocaleDateString()} - {new Date(usage.period_end).toLocaleDateString()}</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Usage */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Monthly Quota Used</span>
                  <span className="text-sm font-medium text-gray-900">
                    {(usage?.monthly_quota_limit || 0) - (usage?.monthly_quota_remaining || 0)} / {usage?.monthly_quota_limit || 0}
                  </span>
                </div>
                <Progress 
                  value={usage?.percentage_used || 0} 
                  className="h-3"
                />
              </div>
              
              {/* Rollover Balance */}
              {usage?.rollover_balance > 0 && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <RefreshCw className="w-4 h-4 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      {usage.rollover_balance} rollover transactions available
                    </p>
                    <p className="text-xs text-blue-600">
                      Expires end of this billing period
                    </p>
                  </div>
                </div>
              )}
              
              {/* Total Available */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Available</span>
                  <span className="text-lg font-bold text-gray-900">
                    {usage?.total_available || 0}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {usage?.monthly_quota_remaining || 0} quota + {usage?.rollover_balance || 0} rollover
                </p>
              </div>
              
              {/* Overage Alert */}
              {usage?.overage_transactions > 0 && (
                <Alert className="bg-orange-50 border-orange-200">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-700">
                    {usage.overage_transactions} overage transactions @ ₹8.50 each = 
                    <span className="font-bold"> ₹{(usage.overage_transactions * 8.5).toFixed(2)}</span> + GST
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Rollover Explanation */}
        {!isDemo && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                How Rollover Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">1</div>
                  <div>
                    <p className="font-medium text-gray-900">Use Monthly Quota First</p>
                    <p className="text-sm text-gray-500">Your fresh {currentPlan?.transactions_per_month || 0} transactions are used first</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">2</div>
                  <div>
                    <p className="font-medium text-gray-900">Then Use Rollover</p>
                    <p className="text-sm text-gray-500">Rollover from last month is used next</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm">3</div>
                  <div>
                    <p className="font-medium text-gray-900">Overage at ₹8.50</p>
                    <p className="text-sm text-gray-500">After quota & rollover, overage applies</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                <Clock className="w-3 h-3 inline mr-1" />
                Up to 50% of your unused monthly quota rolls over. Rollover expires after 1 month if not used.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Plans */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle>Available Plans</CardTitle>
                <CardDescription>Choose a plan that fits your needs. All prices exclude 18% GST.</CardDescription>
              </div>
              <Tabs value={billingCycle} onValueChange={setBillingCycle}>
                <TabsList>
                  <TabsTrigger value="MONTHLY">Monthly</TabsTrigger>
                  <TabsTrigger value="ANNUAL">
                    Annual <Badge className="ml-2 bg-green-100 text-green-700">Save 20%</Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => {
                const isCurrentPlan = currentPlan?.plan_id === plan.plan_id;
                const price = billingCycle === 'MONTHLY' ? plan.monthly_price : plan.annual_price;
                const monthlyEquivalent = billingCycle === 'ANNUAL' ? Math.round(price / 12) : price;
                const rolloverCap = Math.floor(plan.transactions_per_month * (plan.rollover_cap_percent || 50) / 100);
                
                return (
                  <Card 
                    key={plan.plan_id} 
                    className={`relative ${isCurrentPlan ? 'border-blue-500 border-2' : ''}`}
                  >
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-blue-600 text-white">Current Plan</Badge>
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-gray-900">
                          {formatPrice(monthlyEquivalent)}
                        </span>
                        <span className="text-gray-500">/mo</span>
                      </div>
                      <p className="text-xs text-gray-400">+ 18% GST</p>
                      {billingCycle === 'ANNUAL' && (
                        <p className="text-xs text-green-600 font-medium">
                          Billed {formatPrice(price)} annually
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="pb-2">
                      <div className="mb-3 p-2 bg-blue-50 rounded text-center">
                        <p className="text-lg font-bold text-blue-900">{plan.transactions_per_month}</p>
                        <p className="text-xs text-blue-600">transactions/month</p>
                        <p className="text-xs text-blue-500">+ up to {rolloverCap} rollover</p>
                      </div>
                      <ul className="space-y-2">
                        {plan.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                        {/* Coming Soon Features */}
                        {plan.coming_soon_features?.map((feature, idx) => (
                          <li key={`cs-${idx}`} className="flex items-start gap-2 text-sm">
                            <Construction className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-500">
                              {feature}
                              <Badge className="ml-1.5 px-1.5 py-0 text-[10px] bg-amber-100 text-amber-700 border-amber-200">
                                Coming Soon
                              </Badge>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        className={`w-full ${isCurrentPlan ? 'bg-gray-100 text-gray-500' : 'bg-blue-900 hover:bg-blue-800 text-white'}`}
                        disabled={isCurrentPlan || plan.tier === 'ENTERPRISE'}
                        onClick={() => handleSubscribe(plan)}
                      >
                        {isCurrentPlan ? 'Current Plan' : plan.tier === 'ENTERPRISE' ? 'Contact Sales' : 'Subscribe'}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
            
            {/* Enterprise CTA */}
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-900 to-blue-700 rounded-lg text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-bold">Need Enterprise?</h3>
                  <p className="text-blue-100 text-sm">Custom pricing, unlimited features, dedicated support</p>
                </div>
                <Button variant="secondary" className="bg-white text-blue-900 hover:bg-blue-50">
                  Contact Sales
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Invoice History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No invoices yet</p>
            ) : (
              <div className="divide-y">
                {invoices.map((invoice) => (
                  <div key={invoice.invoice_id} className="py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(invoice.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={invoice.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                        {invoice.status}
                      </Badge>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {formatPrice(invoice.total, false)}
                        </p>
                        <p className="text-xs text-gray-500">incl. GST</p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subscribe to {selectedPlan?.name}</DialogTitle>
            <DialogDescription>
              Base price: {formatPrice(billingCycle === 'MONTHLY' ? selectedPlan?.monthly_price : selectedPlan?.annual_price)}
              {billingCycle === 'MONTHLY' ? ' per month' : ' per year'}
              <br />
              <span className="text-xs">+ 18% GST = {formatPrice(billingCycle === 'MONTHLY' ? selectedPlan?.monthly_price : selectedPlan?.annual_price, true)}</span>
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Plan includes:</h4>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600" />
                    {selectedPlan.transactions_per_month} transactions/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                    Up to {Math.floor(selectedPlan.transactions_per_month * 0.5)} rollover transactions
                  </li>
                  {selectedPlan.features.slice(2).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-600" />
                      {feature}
                    </li>
                  ))}
                  {/* Coming Soon Features in Dialog */}
                  {selectedPlan.coming_soon_features?.map((feature, idx) => (
                    <li key={`cs-${idx}`} className="flex items-center gap-2 text-sm text-gray-500">
                      <Construction className="w-4 h-4 text-amber-500" />
                      {feature}
                      <Badge className="ml-1 px-1.5 py-0 text-[10px] bg-amber-100 text-amber-700">Coming Soon</Badge>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Overage: ₹8.50 + GST per transaction beyond quota & rollover
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmSubscription}
              disabled={subscribing}
              className="bg-blue-900 hover:bg-blue-800"
            >
              {subscribing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscribe Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Billing;
